import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect, useSelector } from "react-redux";
import { Auth } from "@connect-xyz/auth-react";
import { CryptoAccountLink } from "@zerohash-sdk/crypto-account-link-react";
import { CryptoSell } from "@zerohash-sdk/crypto-sell-react";
import { CspCryptoSell } from "@zerohash-sdk/csp-crypto-sell-react";
import { CryptoBuy } from "@zerohash-sdk/crypto-buy-react";
import { Onboarding } from "@zerohash-sdk/onboarding-react";
import { FiatDeposits } from "@zerohash-sdk/fiat-deposits-react";
import { FiatWithdrawals } from "@zerohash-sdk/fiat-withdrawals-react";
import { CryptoWithdrawals } from "@zerohash-sdk/crypto-withdrawals-react";
import { CspCryptoWithdrawals } from "@zerohash-sdk/csp-crypto-withdrawals-react";
import { CspFiatWithdrawals } from "@zerohash-sdk/csp-fiat-withdrawals-react";
import { FiatAccountLink } from "@zerohash-sdk/fiat-account-link-react";
import { Fund } from "@zerohash-sdk/fund-react";
import { FundWithdrawals } from "@zerohash-sdk/fund-withdrawals-react";
import { Profile } from "@zerohash-sdk/profile-react";
import { TravelRule } from "@zerohash-sdk/travel-rule-react";
import { createAuthToFundCallbacks } from "../utils/auth-to-fund-mapper";

import {
  containerMediaStyles,
  containerStyle,
  minWidthMatcher,
  screenSizes,
  appWrapperStyle as defaultAppWrapperStyle,
  modalStyle as defaultModalStyle,
  iframeWrapperStyle as defaultIframeWrapperStyle,
  iframeStyle as defaultIframeStyle,
  getModalStyle,
} from "../styles";
import { appCompleted, appFailed, appLoaded, closeModal } from "../redux/actions";
import {
  AppIdentifier,
  appIdentifierToActionPrefixMap,
  AuthSettings,
  Environment,
  InternalEnvironment,
  Filters,
  IncomingMessageType,
  OnboardingPage,
  Page,
  Theme,
} from "../types";
import { useWindowSize } from "../hooks/use-window-size";
import { isInsideWorldApp } from "../utils/world-app-utils";
import {
  PayCommandInput,
  Tokens,
  tokenToDecimals,
} from "@worldcoin/minikit-js";
import { useStyleUpdates } from "./hooks/use-style-updates";
import { getNewSdkEnv, toAuthEnv } from "../new-sdk/env";
import { decodeJWT } from "../api/convert-token";
import { SDK_VERSION } from "../version";

interface AppContainerProps {
  isAppActive?: boolean;
  isAppLoaded?: boolean;
  jwt?: string;
  zeroHashAppURL: string;
  appIdentifier: AppIdentifier;
  filters?: Filters;
  navigate?: Page;
  useAuth?: boolean;
  authSettings?: AuthSettings;
  useNewSdk?: boolean;
  theme?: Theme;
  /**
   * Explicit environment forwarded by `ZeroHashSDK`. When provided, it is
   * preferred over hostname inference from `zeroHashAppURL`.
   */
  env?: Environment | InternalEnvironment;
  /**
   * Travel Rule-only. Identifier of the deposit whose travel-rule information
   * the flow should collect. Sourced from the `travel-rule` reducer, which is
   * populated by `sdk.openModal({ appIdentifier: 'travel-rule', depositId })`.
   */
  depositId?: string;
  /**
   * Travel Rule-only. Wallet address whose travel-rule information the flow
   * should collect. Sourced from the `travel-rule` reducer, which is populated
   * by `sdk.openModal({ appIdentifier: 'travel-rule', walletAddress })`.
   */
  walletAddress?: string;
}

interface AppContainerMappedProps {
  [AppIdentifier.CRYPTO_WITHDRAWALS]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
  };
  [AppIdentifier.ONBOARDING]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
    navigate?: OnboardingPage;
  };
  [AppIdentifier.FIAT_DEPOSITS]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
  };
  [AppIdentifier.FIAT_WITHDRAWALS]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
  };
  [AppIdentifier.CRYPTO_BUY]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
  };
  [AppIdentifier.CRYPTO_SELL]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
  };
  [AppIdentifier.CSP_CRYPTO_WITHDRAWALS]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
  };
  [AppIdentifier.CSP_FIAT_WITHDRAWALS]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
  };
  [AppIdentifier.CSP_CRYPTO_SELL]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
  };
  [AppIdentifier.FUND]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
    useAuth?: boolean;
    authSettings?: AuthSettings;
  };
  [AppIdentifier.PROFILE]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
  };
  [AppIdentifier.CRYPTO_ACCOUNT_LINK]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
  };
  [AppIdentifier.CRYPTO_ACCOUNT_LINK_PAYOUTS]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
  };
  [AppIdentifier.PAYOUTS]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
  };
  [AppIdentifier.PAY]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
  };
  [AppIdentifier.FIAT_ACCOUNT_LINK]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
  };
  [AppIdentifier.TRAVEL_RULE]: {
    isAppActive: boolean;
    isAppLoaded: boolean;
    jwt: string;
    filters?: Filters;
    depositId?: string;
    walletAddress?: string;
  };
  "new-sdk-flags": {
    flags: Partial<Record<AppIdentifier, boolean>>;
  };
  theme: {
    value: Theme;
  };
}

const appLoadTime = Date.now();

/**
 * Each `@zerohash-sdk/*-react` package exports a component with its own
 * domain-specific callback payload types (e.g. `CryptoSellCompletedData`,
 * `CryptoBuyEvent`). A shared interface would have to either widen those
 * callbacks (losing type safety at the call site) or enumerate every domain
 * type (duplicating upstream). We keep `React.FC<any>` and read callback
 * payloads as `unknown` at the render site below — honest about the lack
 * of verification, and safe because we don't dereference.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const newSdkComponentMap: Partial<Record<AppIdentifier, React.FC<any>>> = {
  [AppIdentifier.CRYPTO_ACCOUNT_LINK]: CryptoAccountLink,
  [AppIdentifier.CRYPTO_ACCOUNT_LINK_PAYOUTS]: CryptoAccountLink,
  [AppIdentifier.CRYPTO_SELL]: CryptoSell,
  [AppIdentifier.CSP_CRYPTO_SELL]: CspCryptoSell,
  [AppIdentifier.CRYPTO_BUY]: CryptoBuy,
  [AppIdentifier.ONBOARDING]: Onboarding,
  [AppIdentifier.FIAT_DEPOSITS]: FiatDeposits,
  [AppIdentifier.FIAT_WITHDRAWALS]: FiatWithdrawals,
  [AppIdentifier.CSP_FIAT_WITHDRAWALS]: CspFiatWithdrawals,
  [AppIdentifier.CRYPTO_WITHDRAWALS]: CryptoWithdrawals,
  [AppIdentifier.CSP_CRYPTO_WITHDRAWALS]: CspCryptoWithdrawals,
  [AppIdentifier.FIAT_ACCOUNT_LINK]: FiatAccountLink,
  [AppIdentifier.FUND]: Fund,
  [AppIdentifier.PAY]: Fund,
  [AppIdentifier.PAYOUTS]: FundWithdrawals,
  [AppIdentifier.PROFILE]: Profile,
  [AppIdentifier.TRAVEL_RULE]: TravelRule,
};

// Required JWT sub-fields that mark a withdrawals JWT as a fund-withdrawals JWT.
// Mirrors `requiredFundWithdrawalProps` in sdk-ui-apps/src/shared/constants/constants.ts.
const REQUIRED_FUND_WITHDRAWAL_PROPS: ReadonlyArray<
  "withdrawal_request_amount" | "external_account_id" | "quoted_asset"
> = ["withdrawal_request_amount", "external_account_id", "quoted_asset"];

/**
 * Resolves which `@zerohash-sdk/*-react` component should render for a given
 * AppIdentifier + JWT pair. Most apps map 1:1 via `newSdkComponentMap`, but
 * `CRYPTO_WITHDRAWALS` is overloaded: when the JWT carries `withdrawal_details`,
 * it represents a fund-withdrawals product instead of legacy crypto-withdrawals.
 *
 * `PAYOUTS` always renders FundWithdrawals with `isPayouts=true` (set at the
 * render site, since `isPayouts` isn't part of the shared component prop shape).
 *
 * Validation matches sdk-ui-apps/src/sdks/crypto-withdrawals/crypto-withdrawals-logical-wrapper.tsx:85-128
 * — per-field `console.error` for any missing required sub-field. We still
 * render FundWithdrawals on partial JWTs so the SDK's own error UI surfaces
 * the issue, instead of silently falling back to a different product.
 */
// `env: string` is wider than the public SDK type so internal envs like 'gating'
// work at runtime without being exported.
type ResolvedSdkComponent = React.FC<{ env: string; [key: string]: unknown }>;

function resolveNewSdkComponent(
  appIdentifier: AppIdentifier,
  jwt: string | undefined
): ResolvedSdkComponent | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cast = (c: React.FC<any> | undefined): ResolvedSdkComponent | undefined => c as ResolvedSdkComponent | undefined;
  if (appIdentifier === AppIdentifier.CRYPTO_WITHDRAWALS && jwt) {
    const decoded = decodeJWT(jwt);
    const withdrawalDetails = decoded?.payload?.withdrawal_details;
    if (withdrawalDetails) {
      for (const prop of REQUIRED_FUND_WITHDRAWAL_PROPS) {
        if (withdrawalDetails[prop] === undefined) {
          console.error(`the '${prop}' was not found in the JWT payload`);
        }
      }
      return cast(FundWithdrawals);
    }
  }
  return cast(newSdkComponentMap[appIdentifier]);
}

/**
 * Extra props passed to specific new-SDK components beyond the shared set
 * (jwt, env, theme, callbacks). Most components take none; the few that do:
 *   - FundWithdrawals: `isPayouts` boolean — CRYPTO_WITHDRAWALS (w/ withdrawal_details JWT) → false; PAYOUTS → true.
 *   - CryptoAccountLink: `isPayouts` boolean — CRYPTO_ACCOUNT_LINK → false; CRYPTO_ACCOUNT_LINK_PAYOUTS → true.
 *   - Fund: `isPay` boolean — FUND → false; PAY (pay-sdk scope, single-deposit polling) → true.
 *   - CryptoBuy: optional `filters` forwarded from the SDK's setFilters/openModal config; the
 *     wrapper sets it on the underlying web component via ref so the object reaches the iframe.
 *   - TravelRule: required `depositId` forwarded from the SDK's `openModal({ depositId })`
 *     call; passed through to the underlying web component so it knows which deposit to
 *     collect Travel Rule information for. Also forwards `walletAddress` from
 *     `openModal({ walletAddress })` in the same way.
 */
function getNewSdkExtraProps(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  NewSdkComponent: React.FC<any>,
  appIdentifier: AppIdentifier,
  filters: Filters | undefined,
  depositId: string | undefined,
  walletAddress: string | undefined,
): Record<string, unknown> {
  switch (NewSdkComponent) {
    case FundWithdrawals:
      return { isPayouts: appIdentifier === AppIdentifier.PAYOUTS };
    case CryptoAccountLink:
      return {
        isPayouts: appIdentifier === AppIdentifier.CRYPTO_ACCOUNT_LINK_PAYOUTS,
      };
    case Fund:
      return { isPay: appIdentifier === AppIdentifier.PAY };
    case CryptoBuy:
      return { filters };
    case TravelRule:
      return { depositId, walletAddress };
    default:
      return {};
  }
}

const mapAppToTitle = {
  [AppIdentifier.ONBOARDING]: "Onboarding",
  [AppIdentifier.CRYPTO_WITHDRAWALS]: "Crypto Withdrawals",
  [AppIdentifier.FIAT_DEPOSITS]: "Fiat Deposits",
  [AppIdentifier.FIAT_WITHDRAWALS]: "Fiat Withdrawals",
  [AppIdentifier.CRYPTO_BUY]: "Crypto Buy",
  [AppIdentifier.CRYPTO_SELL]: "Crypto Sell",
  [AppIdentifier.CSP_CRYPTO_WITHDRAWALS]: "CBP Crypto Withdrawals",
  [AppIdentifier.CSP_FIAT_WITHDRAWALS]: "CBP Fiat Withdrawals",
  [AppIdentifier.CSP_CRYPTO_SELL]: "Crypto Sell",
  [AppIdentifier.FUND]: "Fund",
  [AppIdentifier.PROFILE]: "Profile",
  [AppIdentifier.CRYPTO_ACCOUNT_LINK]: "Crypto Account Link",
  [AppIdentifier.CRYPTO_ACCOUNT_LINK_PAYOUTS]: "Crypto Account Link Payouts",
  [AppIdentifier.PAYOUTS]: "Payouts",
  [AppIdentifier.PAY]: "Pay",
  [AppIdentifier.FIAT_ACCOUNT_LINK]: "Fiat Account Link",
  [AppIdentifier.TRAVEL_RULE]: "Travel Rule",
};

const AppContainer = ({
  isAppActive,
  isAppLoaded,
  jwt,
  zeroHashAppURL,
  appIdentifier,
  filters,
  navigate,
  useAuth,
  authSettings,
  useNewSdk,
  theme,
  env: explicitEnv,
  depositId,
  walletAddress,
}: AppContainerProps) => {
  const title = mapAppToTitle[appIdentifier];
  const hasJwt = !!jwt;
  const [container, setContainerStyle] = useState(containerStyle);
  const allAppsState = useSelector((state) => state as AppContainerMappedProps);
  const { height } = useWindowSize();

  // Styles received from iframe via postMessage
  const { styles, stylesLoaded, handleStyleConfig, markStylesLoaded } =
    useStyleUpdates(zeroHashAppURL, {
      appWrapperStyle: defaultAppWrapperStyle,
      modalStyle: defaultModalStyle,
      iframeWrapperStyle: defaultIframeWrapperStyle,
      iframeStyle: defaultIframeStyle,
    });

  // Resolve Auth's env from the same source the new-SDK components use:
  // explicit `env` from the SDK constructor wins, then hostname inference.
  // The previous `zeroHashAppURL.includes("sandbox" | "cert")` substring
  // check silently fell through to "production" for dev hosts, pointing
  // Fund's Auth flow at `api.connect.xyz` instead of the sandbox API.
  const authEnv = toAuthEnv(explicitEnv ?? getNewSdkEnv(zeroHashAppURL));

  /**
   * setContainer sets the CSS styles based on the current matching media queries
   */
  const setContainer = () => {
    const sizeId = minWidthMatcher();
    const containerMediaStyle = containerMediaStyles[sizeId];
    setContainerStyle({ ...containerStyle, ...containerMediaStyle });
  };

  const iRef: React.MutableRefObject<HTMLIFrameElement | null> =
    useRef<HTMLIFrameElement | null>(null);
  useEffect(() => {
    // Send events to the iframe when Apps are ready
    if (
      zeroHashAppURL &&
      isAppLoaded &&
      hasJwt &&
      iRef.current?.contentWindow
    ) {
      // This loops all registered apps and send events listed below.
      Object.keys(allAppsState).forEach((key: string) => {
        if (!allAppsState[key as AppIdentifier].jwt) return;

        // Send JWT token
        iRef.current?.contentWindow?.postMessage(
          {
            type: `${appIdentifierToActionPrefixMap.get(
              key as AppIdentifier,
            )}SEND_JWT_TOKEN`,
            jwt: allAppsState[key as AppIdentifier].jwt,
          },
          zeroHashAppURL,
        );

        // Send zh-web-sdk version for Faro log attribution
        iRef.current?.contentWindow?.postMessage(
          {
            type: `${appIdentifierToActionPrefixMap.get(
              key as AppIdentifier,
            )}SEND_SDK_VERSION`,
            version: SDK_VERSION,
          },
          zeroHashAppURL,
        );

        // If the zh-web-sdk is being run inside the WorldApp, we must send the WorldApp object
        // to the iframe.
        if (isInsideWorldApp()) {
          iRef.current?.contentWindow?.postMessage(
            { type: `WORLD_APP_OBJECT`, WorldApp: window.WorldApp },
            zeroHashAppURL,
          );
        }

        // Send filters
        if (allAppsState[key as AppIdentifier].filters) {
          iRef.current?.contentWindow?.postMessage(
            {
              type: `${appIdentifierToActionPrefixMap.get(
                key as AppIdentifier,
              )}SEND_FILTERS`,
              filters: allAppsState[key as AppIdentifier].filters,
            },
            zeroHashAppURL,
          );
        }
        // Send navigate to Onboarding App
        if (appIdentifier === AppIdentifier.ONBOARDING && navigate) {
          iRef.current?.contentWindow?.postMessage(
            {
              type: `${appIdentifierToActionPrefixMap.get(
                AppIdentifier.ONBOARDING,
              )}NAVIGATE`,
              navigate,
            },
            zeroHashAppURL,
          );
        }
      });
    }
  }, [
    appIdentifier,
    hasJwt,
    isAppLoaded,
    jwt,
    zeroHashAppURL,
    allAppsState,
    navigate,
  ]);

  useEffect(() => {
    // set the styles when the screen size changes
    setContainer();
    if (window) {
      screenSizes.forEach(({ size }) => {
        window
          .matchMedia(`(min-width: ${size}px)`)
          .addEventListener("change", setContainer);
      });
      return () =>
        screenSizes.forEach(({ size }) => {
          window
            .matchMedia(`(min-width: ${size}px)`)
            .removeEventListener("change", setContainer);
        });
    }
  }, []);

  /**
   * Handles MiniKit pay command processing
   */
  const handleMiniKitPayCommand = useCallback(
    async (event: MessageEvent) => {
      const zhAppsURL = new URL(zeroHashAppURL);
      iRef.current?.contentWindow?.postMessage(
        {
          type: "MINIKIT_PAY_COMMAND_RECEIVED",
          payload: {
            origin: event.origin,
            zhAppsURLOrigin: zhAppsURL.origin,
            data: event.data,
          },
        },
        zeroHashAppURL,
      );

      if (!window.MiniKit) {
        iRef.current?.contentWindow?.postMessage(
          {
            type: "MINIKIT_PAY_COMMAND_CANCELED",
            payload: { reason: "window.Minikit not found" },
          },
          zeroHashAppURL,
        );
        return;
      }

      try {
        if (event.data?.type === "MINIKIT_PAY_COMMAND") {
          const payload = event.data.payload;
          const minikitPayload: PayCommandInput = {
            reference: payload.reference,
            to: payload.to,
            tokens: [
              {
                symbol: payload.token as Tokens,
                token_amount: tokenToDecimals(
                  Number(payload.amount),
                  payload.token,
                ).toString(),
              },
            ],
            description: payload.description,
          };
          iRef.current?.contentWindow?.postMessage(
            {
              type: "MINIKIT_PAY_COMMAND_SUBMITTING_PAYMENT",
              payload: { minikitPayload },
            },
            zeroHashAppURL,
          );
          const { finalPayload } =
            await window.MiniKit.commandsAsync.pay(minikitPayload);

          iRef.current?.contentWindow?.postMessage(
            { type: "MINIKIT_PAY_COMMAND_RESULT", payload: { finalPayload } },
            zeroHashAppURL,
          );
        }
      } catch (error) {
        iRef.current?.contentWindow?.postMessage(
          {
            type: "MINIKIT_PAY_COMMAND_ERROR",
            payload: { error, status: "error" },
          },
          zeroHashAppURL,
        );
      }
    },
    [zeroHashAppURL],
  );

  /**
   * Main postMessage event handler that routes messages to appropriate handlers
   */
  const postMessageEventHandler = useCallback(
    async (event: MessageEvent) => {
      // Handle style configuration messages
      if (event.data.type === IncomingMessageType.StyleConfig) {
        handleStyleConfig(event.data, event.origin);
        return;
      }

      // Handle MiniKit pay commands
      await handleMiniKitPayCommand(event);
    },
    [handleStyleConfig, handleMiniKitPayCommand],
  );

  useEffect(() => {
    window.addEventListener("message", postMessageEventHandler);
    return () => window.removeEventListener("message", postMessageEventHandler);
  }, [postMessageEventHandler, zeroHashAppURL]);

  const iframeURL = new URL(zeroHashAppURL);
  iframeURL.searchParams.set("name", appLoadTime.toString());
  if (window) {
    iframeURL.searchParams.set("origin", window.location.origin);
  }

  if (!isAppActive) {
    return <></>;
  }
  const { appWrapperStyle, modalStyle, iframeWrapperStyle, iframeStyle } =
    styles;

  // If useNewSdk is true and a React component is available, render the new SDK component
  const NewSdkComponent = resolveNewSdkComponent(appIdentifier, jwt);
  if (useNewSdk && hasJwt && NewSdkComponent) {
    // Prefer the explicit `env` passed by the host page; fall back to
    // hostname inference from `zeroHashAppURL` for hosts that haven't
    // migrated to the explicit option yet.
    const env = explicitEnv ?? getNewSdkEnv(zeroHashAppURL);
    const newSdkProps = getNewSdkExtraProps(
      NewSdkComponent,
      appIdentifier,
      filters,
      depositId,
      walletAddress,
    );
    return (
      <div
        style={{
          ...appWrapperStyle,
          maxHeight: CSS.supports("height: 100dvh") ? "100dvh" : `${height}px`,
          height: CSS.supports("height: 100dvh") ? "100dvh" : `${height}px`,
        }}
        onClick={() => closeModal(appIdentifier)}
      >
        <div
          style={{
            ...getModalStyle(theme),
            ...container,
            opacity: stylesLoaded ? 1 : 0,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ ...iframeWrapperStyle, ...iframeStyle }}>
            <NewSdkComponent
              jwt={jwt}
              env={env}
              theme={theme ?? "light"}
              {...newSdkProps}
              onLoaded={() => {
                appLoaded(appIdentifier);
                markStylesLoaded();
              }}
              onCompleted={() => {
                appCompleted(appIdentifier);
                // Note: modal stays open so user can see the success screen; they close it themselves via onClose
              }}
              onError={() => {
                appFailed(appIdentifier);
              }}
              onClose={() => {
                closeModal(appIdentifier);
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // If useAuth is true, render Auth component instead of iframe
  if (useAuth && hasJwt) {
    const authCallbacks = createAuthToFundCallbacks(window.location.origin);
    const theme = authSettings?.theme || "auto";

    return (
      <div
        style={{
          ...appWrapperStyle,
          maxHeight: CSS.supports("height: 100dvh") ? "100dvh" : `${height}px`,
          height: CSS.supports("height: 100dvh") ? "100dvh" : `${height}px`,
        }}
        onClick={() => closeModal(appIdentifier)}
      >
        <div
          style={{
            ...getModalStyle(theme),
            ...container,
            zIndex: "9999",
            opacity: stylesLoaded ? 1 : 0,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ ...iframeWrapperStyle, ...iframeStyle }}>
            <Auth jwt={jwt} env={authEnv} theme={theme} {...authCallbacks} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        ...appWrapperStyle,
        /**
         * If browser does not support 100dvh, use height in pixels that is recalculated when
         * the window is resized.
         */
        maxHeight: CSS.supports("height: 100dvh") ? "100dvh" : `${height}px`,
        height: CSS.supports("height: 100dvh") ? "100dvh" : `${height}px`,
      }}
      onClick={() => closeModal(appIdentifier)}
    >
      <div
        style={{ ...modalStyle, ...container, opacity: stylesLoaded ? 1 : 0 }}
      >
        <div style={iframeWrapperStyle}>
          <iframe
            ref={iRef}
            title={title}
            src={iframeURL.toString()}
            allow="camera *; fullscreen *; accelerometer *; gyroscope *; magnetometer *; local-network-access *;"
            sandbox="allow-downloads allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts allow-top-navigation"
            style={iframeStyle}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (
  state: AppContainerMappedProps,
  ownProps: AppContainerProps,
) => {
  return {
    isAppActive: state[ownProps.appIdentifier].isAppActive,
    isAppLoaded: state[ownProps.appIdentifier].isAppLoaded,
    jwt: state[ownProps.appIdentifier].jwt,
    filters: state[ownProps.appIdentifier].filters,
    navigate:
      ownProps.appIdentifier === AppIdentifier.ONBOARDING
        ? state[ownProps.appIdentifier].navigate
        : undefined,
    useAuth:
      ownProps.appIdentifier === AppIdentifier.FUND
        ? state[ownProps.appIdentifier].useAuth
        : undefined,
    authSettings:
      ownProps.appIdentifier === AppIdentifier.FUND
        ? state[ownProps.appIdentifier].authSettings
        : undefined,
    useNewSdk: state["new-sdk-flags"]?.flags?.[ownProps.appIdentifier] ?? false,
    theme: state.theme?.value ?? "light",
    depositId:
      ownProps.appIdentifier === AppIdentifier.TRAVEL_RULE
        ? state[ownProps.appIdentifier].depositId
        : undefined,
    walletAddress:
      ownProps.appIdentifier === AppIdentifier.TRAVEL_RULE
        ? state[ownProps.appIdentifier].walletAddress
        : undefined,
  };
};

export default connect(mapStateToProps)(AppContainer);
